import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Snackbar } from "@mui/material";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import Modal from "../../../components/Modal";
import {
  createCampaign,
  dispatchCampaign,
  getCampaign,
  previewCampaignAudience,
  testCampaign,
  updateCampaign,
  renderCampaignMail,
} from "../../../api/campaigns.service";
import { translateToLangs } from "../../../api/translation.service";
import {
  CATEGORIES,
  CHANNELS,
  LANGS,
  STATUS,
  emptyLangMap,
  fromTranslations,
  stripHtml,
  toTranslations,
} from "../shared";
import Audience, { audienceSentence } from "./Audience";
import BlockEditor from "./BlockEditor";
import ImageDropField from "./ImageDropField";
import DestinationField from "./DestinationField";
import Preview from "./Preview";
import Progress from "./Progress";
import SectionsEditor from "./SectionsEditor";
import TemplatePicker from "./TemplatePicker";
import { blocksToBodies, blocksToHtml, bodiesToBlocks } from "./blocks";
import styles from "./Editor.module.sass";

const LANG_NAMES = { es: "Español", en: "English", fr: "Français", pt: "Português", ru: "Русский" };

/** Formatos de render del canal mail (los que entiende MailBodyRenderer). */
const MAIL_TEMPLATES = [
  { value: "", label: "Corporativa", hint: "Una columna: título, cuerpo y botón." },
  {
    value: "bao",
    label: "Bāo · secciones",
    hint: "Hero con Bāo y secciones numeradas con captura. Para anunciar features.",
  },
  {
    value: "paywall",
    label: "Paywall · Plus+",
    hint: "Cabecera azul con el sello Plus+, galería de pantallas y bloques (planes, porqué). Para vender Plus+.",
  },
];

/** Plantillas de correo que se construyen con secciones. */
const SECTION_TEMPLATES = ["bao", "paywall"];

/** Interruptor de una pieza del mensaje (texto, imagen, botón, webview). */
const Toggle = ({ label, hint, active, disabled, onToggle }) => (
  <button
    type="button"
    disabled={disabled}
    className={cn(styles.toggle, { [styles.toggleActive]: active })}
    onClick={onToggle}
  >
    <span className={styles.toggleSwitch}>
      <span className={styles.toggleKnob} />
    </span>
    <span className={styles.toggleBody}>
      <span className={styles.toggleLabel}>{label}</span>
      <span className={styles.toggleHint}>{hint}</span>
    </span>
  </button>
);
const TITLE_LIMIT = 78;
const PUSH_LIMIT = 178;

const DEFAULT_SEGMENT = {
  inactiveForDays: 75,
  orderBy: { field: "lastPlayed", direction: "desc" },
  limit: 300,
};

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

/**
 * COMPOSITOR de campañas — rediseño "de 0" manteniendo la esencia:
 * topbar de cristal con el nombre inline, TRES PASOS numerados (canales →
 * contenido multipart → audiencia y envío) unidos por un conector, y un rail de
 * preview fijo a la derecha con conmutador de dispositivo por canal.
 */
const CampaignEditor = () => {
  const { id } = useParams();
  const history = useHistory();
  const me = useSelector((s) => s.user?.value);

  const [loading, setLoading] = useState(!!id);
  const [status, setStatus] = useState("draft");
  const [legacy, setLegacy] = useState(false);
  const [audienceExhausted, setAudienceExhausted] = useState(false);

  // Form
  const [name, setName] = useState("");
  const [channels, setChannels] = useState(["push"]);
  const [category, setCategory] = useState("marketing");
  const [segment, setSegment] = useState(DEFAULT_SEGMENT);
  const [batchSize, setBatchSize] = useState(undefined);
  const [titles, setTitles] = useState(emptyLangMap());
  const [blocks, setBlocks] = useState([]);
  const [pushBodies, setPushBodies] = useState(null); // null = auto
  const [linkUrl, setLinkUrl] = useState("");
  // El texto del botón viaja con el mensaje: se traduce como el resto.
  const [linkTitles, setLinkTitles] = useState(emptyLangMap());
  const [webViewUrl, setWebViewUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  // Piezas opcionales del mensaje: cada una se activa a mano en vez de
  // "existe si tiene contenido", que era imposible de leer de un vistazo.
  const [useBody, setUseBody] = useState(true);
  const [useImage, setUseImage] = useState(false);
  const [useButton, setUseButton] = useState(false);
  const [useWebView, setUseWebView] = useState(false);
  // Correo de Bāo: plantilla de render + secciones numeradas con captura.
  const [mailTemplate, setMailTemplate] = useState("");
  const [sections, setSections] = useState([]);

  const [lang, setLang] = useState("es");
  const [contentTab, setContentTab] = useState("rich");
  const [previewChannel, setPreviewChannel] = useState("push");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [toast, setToast] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sending, setSending] = useState(false);
  const [mailKey, setMailKey] = useState(0);
  const [targeted, setTargeted] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  const editable = !legacy && ["draft", "scheduled", "cancelled"].includes(status);
  const showProgress = id && !legacy && ["sending", "paused", "completed", "failed"].includes(status);

  const hasPush = channels.includes("push");
  const hasInapp = channels.includes("inapp");
  const hasRich = channels.includes("inapp") || channels.includes("mail");

  useEffect(() => {
    if (contentTab === "push" && !hasPush) setContentTab("rich");
    if (contentTab === "rich" && !hasRich && hasPush) setContentTab("push");
  }, [channels, contentTab, hasPush, hasRich]);

  // El preview sigue a la pestaña de contenido, pero se puede conmutar a mano.
  useEffect(() => {
    if (!channels.includes(previewChannel)) setPreviewChannel(channels[0]);
  }, [channels, previewChannel]);
  useEffect(() => {
    if (contentTab === "push" && hasPush) setPreviewChannel("push");
    if (contentTab === "rich" && hasRich)
      setPreviewChannel(channels.includes("inapp") ? "inapp" : "mail");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentTab]);

  const applyCampaign = (c) => {
    setName(c.name || "");
    const chs = c.channels?.length ? c.channels : c.channel ? [c.channel] : ["push"];
    setChannels(chs);
    setCategory(c.category || "marketing");
    setSegment(c.segment || {});
    setBatchSize(c.batchSize || undefined);
    setTitles(fromTranslations(c.title));
    setBlocks(bodiesToBlocks(fromTranslations(c.body)));
    setPushBodies(c.pushBody?.length ? fromTranslations(c.pushBody) : null);
    setLinkUrl(c.linkUrl || "");
    const loadedLinkTitles = c.linkTitleTrans?.length
      ? fromTranslations(c.linkTitleTrans)
      : { ...emptyLangMap(), ...(c.linkTitle ? { es: c.linkTitle, en: c.linkTitle } : {}) };
    setLinkTitles(loadedLinkTitles);
    setWebViewUrl(c.webViewUrl || "");
    setImageUrl(c.imageUrl || "");
    setUseBody((c.body || []).some((t) => (t?.trans || "").trim()));
    setUseImage(!!c.imageUrl);
    setUseButton(!!c.linkUrl);
    setUseWebView(!!c.webViewUrl);
    setMailTemplate(c.template || "");
    setSections(
      (c.sections || []).map((sec) => ({
        title: fromTranslations(sec.title),
        body: fromTranslations(sec.body),
        imageUrl: sec.imageUrl || "",
        // Captura por idioma (imageUrlTrans). Sin esto, guardar desde aquí
        // borraba las capturas localizadas de la campaña.
        imageByLang: fromTranslations(sec.imageUrlTrans),
      }))
    );
    setStatus(c.status || "draft");
    setLegacy(!!c.legacy);
    setAudienceExhausted(!!c.audienceExhausted);
    setMailKey((k) => k + 1);
  };

  const load = async () => {
    try {
      const resp = await getCampaign(id);
      if (resp?.data?._id) applyCampaign(resp.data);
      else setToast({ severity: "error", msg: "Campaña no encontrada" });
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo cargar la campaña" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleChannel = (value) => {
    if (!editable) return;
    setChannels((prev) => {
      const next = prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value];
      return next.length === 0 ? prev : next;
    });
  };

  const autoPushText = (l) => {
    const html = blocksToHtml(blocks, l) || "";
    return stripHtml(html).replace(/\s+/g, " ").trim().slice(0, PUSH_LIMIT);
  };
  const pushTextFor = (l) => (pushBodies ? pushBodies[l] || "" : autoPushText(l));

  /** Estado en vivo de cada canal (para la tarjeta del paso 1). */
  const channelStatus = (value) => {
    if (!channels.includes(value)) return "desactivado";
    if (value === "push") {
      const len = pushTextFor(lang).length;
      return len ? `texto plano · ${len}/${PUSH_LIMIT}` : "texto plano";
    }
    const blocksWithText = blocks.filter((b) => b.type !== "image").length;
    const images = blocks.filter((b) => b.type === "image").length;
    const parts = [];
    if (blocksWithText) parts.push(`${blocksWithText} bloque${blocksWithText > 1 ? "s" : ""}`);
    if (images) parts.push(`${images} img`);
    return parts.length ? `HTML · ${parts.join(" · ")}` : "HTML con formato";
  };

  const payload = () => ({
    name,
    channels,
    category,
    segment,
    batchSize: batchSize || undefined,
    title: toTranslations(titles),
    body: useBody ? toTranslations(blocksToBodies(blocks, titles)) : [],
    pushBody: pushBodies ? toTranslations(pushBodies) : undefined,
    linkUrl: useButton ? linkUrl || undefined : undefined,
    // `linkTitle` plano se sigue mandando: es el fallback de las campañas viejas.
    linkTitle: useButton ? linkTitles[lang] || linkTitles.en || undefined : undefined,
    linkTitleTrans: useButton ? toTranslations(linkTitles) : undefined,
    // El webview es una pantalla DENTRO de la app: sin in-app no existe.
    webViewUrl: hasInapp && useWebView ? webViewUrl || undefined : undefined,
    imageUrl: useImage ? imageUrl || undefined : undefined,
    template: mailTemplate || undefined,
    sections: sections.length
      ? sections.map((sec) => ({
          title: toTranslations(sec.title),
          body: toTranslations(sec.body),
          // imageUrl = captura por defecto (la de ES, o la primera que haya);
          // el backend la usa si falta la del idioma del destinatario.
          imageUrl:
            sec.imageByLang?.es ||
            sec.imageUrl ||
            Object.values(sec.imageByLang || {}).find(Boolean) ||
            undefined,
          imageUrlTrans: (() => {
            const t = toTranslations(sec.imageByLang || {});
            return t.length ? t : undefined;
          })(),
        }))
      : undefined,
  });

  // Vista previa REAL del correo: el servidor lo genera con la misma plantilla que el
  // envío. Se pide con un pequeño retardo para no llamar en cada pulsación.
  const [serverMail, setServerMail] = useState(null);
  const mailPayloadKey = JSON.stringify([payload(), lang]);
  useEffect(() => {
    if (!channels.includes("mail")) return undefined;
    let cancelled = false;
    setServerMail((prev) => ({ ...(prev || {}), loading: true }));
    const timer = setTimeout(async () => {
      try {
        const resp = await renderCampaignMail(payload(), lang);
        if (!cancelled && resp?.data?.html) {
          setServerMail({ subject: resp.data.subject, html: resp.data.html, loading: false });
        } else if (!cancelled) {
          setServerMail(null);
        }
      } catch (e) {
        if (!cancelled) setServerMail(null);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailPayloadKey]);

  /** Vuelca una plantilla ya interpolada sobre el formulario. */
  const applyTemplatePatch = (patch, template) => {
    setName(patch.name);
    setChannels(patch.channels);
    setCategory(patch.category);
    setTitles(patch.titles);
    setBlocks(patch.blocks);
    setPushBodies(patch.pushBodies);
    setLinkUrl(patch.linkUrl);
    setLinkTitles(patch.linkTitles);
    setImageUrl(patch.imageUrl);
    setUseBody(patch.blocks.length > 0);
    setUseImage(!!patch.imageUrl);
    setUseButton(!!patch.linkUrl);
    setUseWebView(false);
    setMailTemplate(patch.mailTemplate);
    setSections(patch.sections);
    setMailKey((k) => k + 1);
    setToast({ severity: "success", msg: `Plantilla «${template.label}» aplicada` });
  };

  const canSave = name.trim() !== "" && toTranslations(titles).length > 0;

  const save = async ({ silent } = {}) => {
    if (!canSave) {
      setToast({ severity: "error", msg: "Falta el nombre o al menos un título" });
      return null;
    }
    setSaving(true);
    try {
      if (id) {
        const resp = await updateCampaign(id, payload());
        if (!resp?.data?._id) throw new Error("update failed");
        if (!silent) setToast({ severity: "success", msg: "Borrador guardado" });
        return id;
      }
      const resp = await createCampaign(payload());
      if (!resp?.data?._id) throw new Error("create failed");
      if (!silent) setToast({ severity: "success", msg: "Borrador creado" });
      history.replace(`/campaigns/${resp.data._id}`);
      return resp.data._id;
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo guardar" });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openSendModal = async () => {
    const savedId = await save({ silent: true });
    if (!savedId) return;
    setSendModal({ targeted: null, overlap: [] });
    try {
      const resp = await previewCampaignAudience(savedId);
      setTargeted(resp?.data?.targeted ?? null);
      setSendModal((prev) =>
        prev ? { targeted: resp?.data?.targeted ?? null, overlap: resp?.data?.overlap ?? [] } : prev
      );
    } catch (e) {
      /* — */
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const resp = await dispatchCampaign(id);
      if (resp?.data && typeof resp.data.targeted === "number") {
        setSendModal(null);
        const extra =
          resp.data.remaining > 0
            ? ` (quedan ${fmtNum(resp.data.remaining)} para próximas tandas)`
            : "";
        setToast({
          severity: "success",
          msg: `Tanda encolada: ${fmtNum(resp.data.targeted)} destinatarios${extra}`,
        });
        load();
      } else {
        setToast({ severity: "error", msg: "No se pudo lanzar la campaña" });
      }
    } catch (e) {
      setToast({ severity: "error", msg: "Error al lanzar la campaña" });
    } finally {
      setSending(false);
    }
  };

  const handleTest = async () => {
    const myId = me?._id || me?.id;
    if (!myId) {
      setToast({ severity: "error", msg: "No se pudo identificar tu usuario" });
      return;
    }
    const savedId = await save({ silent: true });
    if (!savedId) return;
    setTesting(true);
    try {
      const resp = await testCampaign(savedId, myId);
      if (resp?.data?.queued) {
        setToast({
          severity: "success",
          msg: `Prueba enviada por ${channels.map((c) => CHANNELS[c].label).join(" + ")}.`,
        });
      } else {
        setToast({ severity: "error", msg: "No se pudo enviar la prueba" });
      }
    } catch (e) {
      setToast({ severity: "error", msg: "Error al enviar la prueba" });
    } finally {
      setTesting(false);
    }
  };

  const handleTranslate = async () => {
    const source = lang;
    const targets = LANGS.filter((l) => l !== source);
    const hasContent = blocks.some((b) => (b.text?.[source] || "").trim());
    if (!titles[source] && !hasContent && !(pushBodies?.[source] || "").trim()) {
      setToast({ severity: "error", msg: `Escribe primero el contenido en ${source.toUpperCase()}` });
      return;
    }
    setTranslating(true);
    try {
      if (titles[source]) {
        const t = await translateToLangs(titles[source], targets);
        setTitles((prev) => ({ ...prev, ...t }));
      }
      const translated = await Promise.all(
        blocks.map(async (b) => {
          const text = b.text?.[source];
          if (!text || !text.trim()) return b;
          const t = await translateToLangs(text, targets);
          return { ...b, text: { ...b.text, ...t } };
        })
      );
      setBlocks(translated);
      if (pushBodies?.[source]) {
        const t = await translateToLangs(pushBodies[source], targets);
        setPushBodies((prev) => ({ ...prev, ...t }));
      }
      if (linkTitles[source]) {
        const t = await translateToLangs(linkTitles[source], targets);
        setLinkTitles((prev) => ({ ...prev, ...t }));
      }
      // Las secciones del correo de Bāo son parte del mensaje: se traducen igual.
      if (sections.length) {
        const translatedSections = await Promise.all(
          sections.map(async (sec) => {
            const next = { ...sec };
            if (sec.title?.[source]) {
              next.title = { ...sec.title, ...(await translateToLangs(sec.title[source], targets)) };
            }
            if (sec.body?.[source]) {
              next.body = { ...sec.body, ...(await translateToLangs(sec.body[source], targets)) };
            }
            return next;
          })
        );
        setSections(translatedSections);
      }
      setMailKey((k) => k + 1);
      setToast({ severity: "success", msg: "Traducido al resto de idiomas" });
    } catch (e) {
      setToast({ severity: "error", msg: "Error al traducir" });
    } finally {
      setTranslating(false);
    }
  };

  const previewTitle = titles[lang] || titles.en || Object.values(titles).find(Boolean) || "";
  const previewHtml = blocksToHtml(blocks, lang) || blocksToHtml(blocks, "en");
  const statusMeta = STATUS[status] || STATUS.draft;
  const channelSummary = useMemo(
    () => channels.map((c) => CHANNELS[c]?.label || c).join(" + "),
    [channels]
  );
  const pushLen = pushTextFor(lang).length;

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.skeletonBig} />
        <div className={styles.skeletonBig} />
      </div>
    );
  }

  return (
    <>
      {/* ── TOPBAR ── */}
      <div className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <Link className={styles.back} to="/campaigns">
            <Icon name="arrow-left" size="20" />
          </Link>
          <div className={styles.topMain}>
            <div className={styles.topTitleRow}>
              <input
                className={styles.topNameInput}
                placeholder="Nombre interno de la campaña…"
                value={name}
                disabled={!editable}
                onChange={(e) => setName(e.target.value)}
              />
              <span className={cn(statusMeta.className, styles.statusBadge)}>{statusMeta.label}</span>
            </div>
            <div className={styles.topSub}>
              {channelSummary} · {audienceSentence(segment)}
            </div>
          </div>
        </div>
        <div className={styles.topActions}>
          {editable && (
            <>
              <button
                className={cn("button-stroke", styles.templateButton)}
                onClick={() => setTemplateOpen(true)}
                title="Rellena la campaña con un anuncio ya escrito"
              >
                ✨ Plantillas
              </button>
              <button className="button-stroke" onClick={() => save()} disabled={saving}>
                {saving ? "Guardando…" : "Guardar"}
              </button>
              <button
                className={cn("button-stroke", styles.testButton)}
                onClick={handleTest}
                disabled={testing || saving}
                title="Te lo envía solo a ti (todos los canales) para verlo en real"
              >
                {testing ? "Enviando…" : "🧪 Prueba"}
              </button>
              <button className="button" onClick={openSendModal} disabled={saving || !canSave}>
                Enviar
              </button>
            </>
          )}
        </div>
      </div>

      {!editable && !showProgress && (
        <div className={styles.readonlyBanner}>
          <Icon name="info" size="20" />
          Esta campaña ya se envió y es de solo lectura. Puedes duplicarla desde el listado.
        </div>
      )}

      {showProgress && (
        <Card className={styles.statsCard} title="Seguimiento del envío" classTitle="title-green">
          <Progress
            campaignId={id}
            status={status}
            batchSize={batchSize}
            audienceExhausted={audienceExhausted}
            onAction={load}
            onNotify={setToast}
          />
        </Card>
      )}

      <div className={styles.grid}>
        <div className={styles.left}>
          {/* ══ PASO 1 · QUÉ ENVÍO Y POR DÓNDE ══ */}
          <div className={styles.step}>
            <div className={styles.stepNum}>1</div>
            <div className={styles.stepHead}>
              <div className={styles.stepTitle}>El mensaje</div>
              <div className={styles.stepHint}>Elige por dónde sale y escríbelo</div>
            </div>
            <div className={styles.channels}>
              {Object.values(CHANNELS).map((c) => {
                const active = channels.includes(c.value);
                return (
                  <button
                    key={c.value}
                    type="button"
                    disabled={!editable}
                    className={cn(styles.chCard, { [styles.chCardActive]: active })}
                    style={{ "--ch": c.color }}
                    onClick={() => toggleChannel(c.value)}
                    title={c.description}
                  >
                    <span className={styles.chIcon}>
                      <Icon name={c.icon} size="20" />
                    </span>
                    <span className={styles.chBody}>
                      <span className={styles.chName}>{c.label}</span>
                      <span className={styles.chStatus}>{channelStatus(c.value)}</span>
                    </span>
                    <span className={styles.chCheck}>
                      <Icon name="check" size="12" />
                    </span>
                  </button>
                );
              })}
            </div>
            <div className={styles.typeRow}>
              <div className={styles.typeSeg}>
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    disabled={!editable}
                    className={cn(styles.typeOption, {
                      [styles.typeOptionActive]: category === c.value,
                    })}
                    onClick={() => setCategory(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <span className={styles.typeHint}>
                {CATEGORIES.find((c) => c.value === category)?.hint}
              </span>
            </div>

            <div className={styles.langBar}>
              <div className={styles.langTabs}>
              {LANGS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={cn(styles.langTab, { [styles.langTabActive]: lang === l })}
                  onClick={() => setLang(l)}
                  title={LANG_NAMES[l]}
                >
                  {l.toUpperCase()}
                  <span className={cn(styles.langDot, { [styles.langDotOk]: !!titles[l] })} />
                </button>
              ))}
              </div>
              {editable && (
                <button
                  className={cn("button-stroke", "button-small")}
                  onClick={handleTranslate}
                  disabled={translating}
                  title={`Traduce ${lang.toUpperCase()} → resto con DeepL`}
                >
                  {translating ? "Traduciendo…" : `Traducir ${lang.toUpperCase()} → resto`}
                </button>
              )}
            </div>

            <div className={styles.titleField}>
              <input
                className={styles.titleInput}
                placeholder={`Título / asunto en ${LANG_NAMES[lang]}…`}
                value={titles[lang]}
                disabled={!editable}
                onChange={(e) => setTitles({ ...titles, [lang]: e.target.value })}
              />
              <div className={styles.titleMeta}>
                <span
                  className={cn(styles.counter, {
                    [styles.counterOver]: (titles[lang] || "").length > TITLE_LIMIT,
                  })}
                >
                  {(titles[lang] || "").length}/{TITLE_LIMIT}
                </span>
              </div>
            </div>

            {hasPush && hasRich && (
              <div className={styles.contentTabs}>
                <button
                  type="button"
                  className={cn(styles.contentTab, {
                    [styles.contentTabActive]: contentTab === "rich",
                  })}
                  onClick={() => setContentTab("rich")}
                >
                  💬{" "}
                  {channels.includes("inapp") && channels.includes("mail")
                    ? "In-App + Email"
                    : channels.includes("inapp")
                    ? "In-App"
                    : "Email"}
                  <span className={styles.contentTabHint}>con formato</span>
                </button>
                <button
                  type="button"
                  className={cn(styles.contentTab, {
                    [styles.contentTabActive]: contentTab === "push",
                  })}
                  onClick={() => setContentTab("push")}
                >
                  📱 Push <span className={styles.contentTabHint}>texto plano</span>
                </button>
              </div>
            )}

            {useBody && ((contentTab === "rich" && hasRich) || (!hasPush && hasRich)) && (
              <BlockEditor
                blocks={blocks}
                onChange={setBlocks}
                lang={lang}
                version={mailKey}
                disabled={!editable}
                onNotify={setToast}
              />
            )}

            {((contentTab === "push" && hasPush) || (hasPush && !hasRich)) && (
              <div>
                <div className={styles.pushRow}>
                  <label className={styles.pushAuto}>
                    <input
                      type="checkbox"
                      disabled={!editable || !hasRich}
                      checked={!pushBodies}
                      onChange={(e) => {
                        if (e.target.checked) setPushBodies(null);
                        else {
                          const seeded = {};
                          LANGS.forEach((l) => (seeded[l] = autoPushText(l)));
                          setPushBodies(seeded);
                        }
                      }}
                    />
                    Auto desde el contenido{!hasRich ? " (no hay contenido rico)" : ""}
                  </label>
                  <span
                    className={cn(styles.counter, { [styles.counterOver]: pushLen > PUSH_LIMIT })}
                  >
                    {pushLen}/{PUSH_LIMIT}
                  </span>
                </div>
                <textarea
                  className={cn(styles.pushTextarea, {
                    [styles.pushTextareaAuto]: !pushBodies && hasRich,
                  })}
                  rows={3}
                  maxLength={PUSH_LIMIT}
                  placeholder="Texto del push…"
                  value={pushTextFor(lang)}
                  disabled={!editable || (!pushBodies && hasRich)}
                  onChange={(e) => {
                    if (!pushBodies) {
                      const seeded = {};
                      LANGS.forEach((l) => (seeded[l] = autoPushText(l)));
                      seeded[lang] = e.target.value;
                      setPushBodies(seeded);
                    } else {
                      setPushBodies({ ...pushBodies, [lang]: e.target.value });
                    }
                  }}
                />
                <div className={styles.pushMeter}>
                  <div
                    className={cn(styles.pushMeterFill, {
                      [styles.pushMeterHot]: pushLen > PUSH_LIMIT * 0.9,
                    })}
                    style={{ width: `${Math.min(100, (pushLen / PUSH_LIMIT) * 100)}%` }}
                  />
                </div>
                {!pushBodies && hasRich && (
                  <div className={styles.hint}>
                    Se genera solo desde el contenido con formato. Desmarca «Auto» para escribir un
                    push distinto.
                  </div>
                )}
              </div>
            )}

            {toTranslations(titles).length === 0 && (
              <div className={styles.warn}>Añade al menos un título (en cualquier idioma).</div>
            )}

            {/* ── PIEZAS OPCIONALES ─────────────────────────────────────────
                Antes "existían si tenían contenido", que no se veía por ningún
                lado. Ahora cada una se enciende a mano. */}
            <div className={styles.parts}>
              <div className={styles.partsHead}>Piezas del mensaje</div>
              <div className={styles.partsToggles}>
                <Toggle
                  label="Texto"
                  hint="El cuerpo con formato"
                  active={useBody}
                  disabled={!editable}
                  onToggle={() => setUseBody((v) => !v)}
                />
                <Toggle
                  label="Imagen"
                  hint="Cabecera del anuncio"
                  active={useImage}
                  disabled={!editable}
                  onToggle={() => setUseImage((v) => !v)}
                />
                <Toggle
                  label="Botón"
                  hint="CTA con destino"
                  active={useButton}
                  disabled={!editable}
                  onToggle={() => setUseButton((v) => !v)}
                />
                {hasInapp && (
                  <Toggle
                    label="WebView"
                    hint="Página dentro de la app"
                    active={useWebView}
                    disabled={!editable}
                    onToggle={() => setUseWebView((v) => !v)}
                  />
                )}
              </div>

              {useImage && (
                <div className={styles.part}>
                  <div className={styles.label}>Imagen destacada</div>
                  <ImageDropField
                    value={imageUrl}
                    onChange={setImageUrl}
                    disabled={!editable}
                    onNotify={setToast}
                  />
                </div>
              )}

              {useButton && (
                <div className={styles.partSplit}>
                  <div className={styles.part}>
                    <div className={styles.label}>Destino del botón</div>
                    <DestinationField value={linkUrl} onChange={setLinkUrl} disabled={!editable} />
                  </div>
                  <div className={styles.part}>
                    <div className={styles.label}>
                      Texto del botón <span className={styles.langMark}>{lang.toUpperCase()}</span>
                    </div>
                    <input
                      className={styles.input}
                      placeholder={`p. ej. Ver novedades (${LANG_NAMES[lang]})`}
                      value={linkTitles[lang]}
                      disabled={!editable}
                      onChange={(e) => setLinkTitles({ ...linkTitles, [lang]: e.target.value })}
                    />
                    <div className={styles.langDots}>
                      {LANGS.map((l) => (
                        <span
                          key={l}
                          className={cn(styles.langDotSmall, {
                            [styles.langDotOk]: !!linkTitles[l],
                          })}
                          title={`${l.toUpperCase()}: ${linkTitles[l] || "sin traducir"}`}
                        />
                      ))}
                      <span className={styles.langDotsHint}>
                        el botón se traduce con «Traducir {lang.toUpperCase()} → resto»
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {hasInapp && useWebView && (
                <div className={styles.part}>
                  <div className={styles.label}>WebView (se abre dentro de la app)</div>
                  <input
                    className={styles.input}
                    placeholder="https://…"
                    value={webViewUrl}
                    disabled={!editable}
                    onChange={(e) => setWebViewUrl(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Formato del correo: la newsletter de Bāo son secciones numeradas
                con captura, y es la forma de anunciar features. */}
            {channels.includes("mail") && (
              <div className={styles.mailTemplateRow}>
                <div className={styles.label}>Formato del correo</div>
                <div className={styles.typeSeg}>
                  {MAIL_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.value}
                      type="button"
                      disabled={!editable}
                      className={cn(styles.typeOption, {
                        [styles.typeOptionActive]: (mailTemplate || "") === tpl.value,
                      })}
                      onClick={() => setMailTemplate(tpl.value)}
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
                <span className={styles.typeHint}>
                  {MAIL_TEMPLATES.find((tpl) => tpl.value === (mailTemplate || ""))?.hint}
                </span>
              </div>
            )}
          </div>

          {SECTION_TEMPLATES.includes(mailTemplate) && channels.includes("mail") && (
            <div className={styles.step}>
              <div className={styles.stepNum}>2</div>
              <div className={styles.stepHead}>
                <div className={styles.stepTitle}>Las secciones</div>
                <div className={styles.stepHint}>
                  {mailTemplate === "paywall"
                    ? "Con captura: tarjeta de la galería. Sin captura: bloque a ancho completo (planes, porqué…); el botón va en el primero y en el último."
                    : "Una por novedad, con su captura"}
                </div>
              </div>
              <SectionsEditor
                sections={sections}
                onChange={setSections}
                lang={lang}
                disabled={!editable}
                onNotify={setToast}
              />
            </div>
          )}

          {/* ══ PASO FINAL · AUDIENCIA Y ENVÍO ══ */}
          <div className={styles.step}>
            <div className={styles.stepNum}>
              {SECTION_TEMPLATES.includes(mailTemplate) && channels.includes("mail") ? 3 : 2}
            </div>
            <div className={styles.stepHead}>
              <div className={styles.stepTitle}>¿A quién y cómo?</div>
            </div>
            <Audience
              value={segment}
              onChange={setSegment}
              disabled={!editable}
              channel={channels.includes("mail") ? "mail" : channels[0]}
              channels={channels}
              batchSize={batchSize}
              onBatchSize={setBatchSize}
              onTargeted={setTargeted}
            />
          </div>
        </div>

        {/* ── RAIL DE PREVIEW ── */}
        <div className={styles.right}>
          <div className={styles.previewRail}>
            <div className={styles.previewHead}>
              <div className={styles.previewTitle}>Preview en vivo</div>
              <div className={styles.previewLangs}>
                {LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={cn(styles.previewLang, { [styles.previewLangActive]: lang === l })}
                    onClick={() => setLang(l)}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.deviceSwitch}>
              {Object.values(CHANNELS).map((c) => (
                <button
                  key={c.value}
                  type="button"
                  disabled={!channels.includes(c.value)}
                  className={cn(styles.deviceOption, {
                    [styles.deviceOptionActive]: previewChannel === c.value,
                  })}
                  onClick={() => {
                    setPreviewChannel(c.value);
                    // Cambiar de dispositivo TAMBIÉN cambia el editor: es la forma
                    // rápida de saltar entre "texto plano" y "con formato".
                    setContentTab(c.value === "push" ? "push" : "rich");
                  }}
                >
                  <Icon name={c.icon} size="15" />
                  {c.label}
                </button>
              ))}
            </div>

            <div className={styles.previewStage}>
              <Preview
                channel={previewChannel}
                lang={lang}
                title={previewTitle}
                body={previewChannel === "push" ? pushTextFor(lang) : previewHtml}
                bodyHtml={previewHtml}
                imageUrl={useImage ? imageUrl : ""}
                linkUrl={useButton ? linkUrl : ""}
                linkTitle={useButton ? linkTitles[lang] || linkTitles.en : ""}
                webViewUrl={hasInapp && useWebView ? webViewUrl : ""}
                sections={SECTION_TEMPLATES.includes(mailTemplate) ? sections : null}
                serverMail={serverMail}
              />
            </div>
            <div className={styles.previewFoot}>
              Así llega por <strong>{CHANNELS[previewChannel]?.label}</strong> con la app en{" "}
              <strong>{LANG_NAMES[lang]}</strong>.
            </div>
          </div>
        </div>
      </div>

      {/* Confirmación de envío */}
      <Modal visible={!!sendModal} onClose={() => (sending ? null : setSendModal(null))}>
        {sendModal && (
          <div className={styles.confirm}>
            <div className={cn("title-purple", styles.confirmTitle)}>Enviar campaña</div>
            <div className={styles.confirmName}>{name}</div>
            <div className={styles.confirmMeta}>{channelSummary}</div>
            <div className={styles.confirmSentence}>{audienceSentence(segment)}</div>
            <div className={styles.confirmCount}>
              {sendModal.targeted == null ? (
                "Calculando audiencia…"
              ) : (
                <>
                  <strong>{fmtNum(sendModal.targeted)}</strong> destinatarios
                  {batchSize > 0 && sendModal.targeted > batchSize && (
                    <span className={styles.confirmBatches}>
                      {" "}
                      · primera tanda: {fmtNum(batchSize)} · ≈{" "}
                      {Math.ceil(sendModal.targeted / batchSize)} días
                    </span>
                  )}
                </>
              )}
            </div>
            {sendModal.overlap?.length > 0 && (
              <div className={styles.confirmOverlap}>
                ⚠️ Parte de esta audiencia ya recibió otras campañas recientes:
                {sendModal.overlap.slice(0, 3).map((o) => (
                  <div key={o.campaignId} className={styles.confirmOverlapRow}>
                    «{o.name}» — {fmtNum(o.users)} repetidos
                  </div>
                ))}
                <div className={styles.confirmOverlapHint}>
                  Actívales «No repetir» en Audiencia si no quieres repetirles.
                </div>
              </div>
            )}
            <div className={styles.confirmWarn}>
              El envío se encola inmediatamente y no se puede deshacer.
            </div>
            <div className={styles.confirmActions}>
              <button className="button-stroke" onClick={() => setSendModal(null)} disabled={sending}>
                Cancelar
              </button>
              <button className="button" onClick={handleSend} disabled={sending}>
                {sending ? "Enviando…" : "Confirmar envío"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <TemplatePicker
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        onApply={applyTemplatePatch}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default CampaignEditor;
